import * as React from 'react';
import classNames from 'classnames';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Colors, Display } from 'Views/Components/Button/Button';
import { store } from 'Models/Store';
import LoadingEllipses from 'Views/Components/_HumanWritten/Lottie/LoadingEllipses/LoadingEllipses';
import {
	BookingPriceBreakdown,
	BookingPricingDiagnostic,
	FetchBookingPriceBreakdown,
	FetchBookingPricingDiagnostic,
} from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { PriceBreakdownPanel } from './PriceBreakdownPanel';
import { PricingDiagnosticPanel } from './PricingDiagnosticPanel';

export interface PricingModalContentProps {
	bookingId: string;
	total: number;
}

type PricingTab = 'summary' | 'detail';

/**
 * Super-only pricing modal: the reconciling summary and the raw per-alteration diagnostic as two tabs of
 * one modal.
 *
 * <para>They used to be two modals, and reaching the diagnostic meant loading the summary, clicking
 * through, and waiting on a second request that had not started yet. Both requests are now fired together
 * on mount, so the diagnostic is usually already there by the time the tab is clicked, and switching back
 * costs nothing — neither is refetched.</para>
 *
 * <para>They are tracked separately rather than awaited as a pair on purpose: the summary is the far
 * smaller response, so gating it on the diagnostic would make the common case slower than it is today.</para>
 */
export function PricingModalContent({ bookingId, total }: PricingModalContentProps) {
	const [tab, setTab] = React.useState<PricingTab>('summary');

	const [breakdown, setBreakdown] = React.useState<BookingPriceBreakdown | null>(null);
	const [breakdownLoading, setBreakdownLoading] = React.useState(true);

	const [diagnostic, setDiagnostic] = React.useState<BookingPricingDiagnostic | null>(null);
	const [diagnosticLoading, setDiagnosticLoading] = React.useState(true);

	React.useEffect(() => {
		let active = true;

		FetchBookingPriceBreakdown(bookingId).then(result => {
			if (active) {
				setBreakdown(result);
				setBreakdownLoading(false);
			}
		});

		FetchBookingPricingDiagnostic(bookingId).then(result => {
			if (active) {
				setDiagnostic(result);
				setDiagnosticLoading(false);
			}
		});

		return () => {
			active = false;
		};
	}, [bookingId]);

	const loading = tab === 'summary' ? breakdownLoading : diagnosticLoading;

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>Pricing</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				<div className="booking-pricing-modal">
					<div className="booking-pricing-modal__segmented" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={tab === 'summary'}
							className={classNames('booking-pricing-modal__segmented-option', {
								'is-active': tab === 'summary',
							})}
							onClick={() => setTab('summary')}
						>
							Summary
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={tab === 'detail'}
							className={classNames('booking-pricing-modal__segmented-option', {
								'is-active': tab === 'detail',
							})}
							onClick={() => setTab('detail')}
						>
							Full detail
						</button>
					</div>

					{loading && (
						<div className="booking-pricing-modal__loading">
							<LoadingEllipses>
								{tab === 'summary' ? 'Loading breakdown' : 'Loading pricing detail'}
							</LoadingEllipses>
						</div>
					)}

					{!loading && tab === 'summary' && <PriceBreakdownPanel breakdown={breakdown} total={total} />}
					{!loading && tab === 'detail' && <PricingDiagnosticPanel diagnostic={diagnostic} />}
				</div>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.ConfirmButton
					colors={Colors.Secondary}
					display={Display.Solid}
					onClick={() => store.modal?.hide()}
				>
					Close
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}
